/**
 * Project views
 * Path: `@/views/project`
 * - Project's edm views: used for document's managment
 * - Company settings views: handle company settings
 * - Project's inspection views
 * - Project's nonconformity views
 */

import multiguard from "vue-router-multiguard"
import { requireAuth, requireCompany, requireProject } from "@/utils/middleware"

const General = () => import("@/views/project/General.vue")
const Gant = () => import("@/views/project/Gant.vue")
const ProjectCreateUpdate = () => import("@/views/project/ProjectCreateUpdate.vue")
const ProjectFeed = () => import("@/views/project/ProjectFeed.vue")
const Projects = () => import("@/views/project/Projects.vue")
const ProjectTeam = () => import("@/views/project/ProjectTeam.vue")

const Documentation = () => import("@/views/project/edm/Documentation.vue")

const AllInspections = () => import("@/views/project/inspection/AllInspections.vue")
const Inspection = () => import("@/views/project/inspection/Inspection.vue")
const InspectionCreateUpdate = () => import("@/views/project/inspection/InspectionCreateUpdate.vue")
const Inspections = () => import("@/views/project/inspection/Inspections.vue")
const Meeting = () => import("@/views/project/inspection/Meeting.vue")
const MeetingCreateUpdate = () => import("@/views/project/inspection/MeetingCreateUpdate.vue")

const AllActions = () => import("@/views/project/nonconformity/AllActions.vue")
const AllProjectActions = () => import("@/views/project/nonconformity/AllProjectActions.vue")
const AllNonConformities = () => import("@/views/project/nonconformity/AllNonConformities.vue")
const NonConformities = () => import("@/views/project/nonconformity/NonConformities.vue")
const NonConformityCreateUpdate = () =>
  import("@/views/project/nonconformity/NonConformityCreateUpdate.vue")

// const PcqDsi = () => import("@/views/project/pcqDsi/PcqDsi.vue")
const PcqDsiCreateUpdate = () => import("@/views/project/pcqDsi/PcqDsiCreateUpdate.vue")
const PcqDsis = () => import("@/views/project/pcqDsi/PcqDsis.vue")

// DRAWERS
const ProjectDrawer = () => import("@/components/layouts/core/ProjectDrawer.vue")

const InternalNotification = () => import("@/views/project/notification/InternalNotification.vue")

export default [
  {
    path: "/inspections",
    name: "AllInspections",
    component: AllInspections,
    beforeEnter: multiguard([requireAuth]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/actions",
    name: "AllProjectActions",
    component: AllProjectActions,
    beforeEnter: multiguard([requireAuth]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/non-conformities",
    name: "AllNonConformities",
    component: AllNonConformities,
    beforeEnter: multiguard([requireAuth]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/non-conformities/actions",
    name: "AllActions",
    component: AllActions,
    beforeEnter: multiguard([requireAuth]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/projects/list",
    name: "Projects",
    component: Projects,
    beforeEnter: multiguard([requireAuth, requireCompany]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/projects/:id?/editor",
    name: "ProjectCreateUpdate",
    component: ProjectCreateUpdate,
    beforeEnter: multiguard([requireAuth, requireCompany]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/home",
    name: "Project",
    component: General,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/gant-diagram",
    name: "Gantt",
    component: Gant,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/feed",
    name: "ProjectFeed",
    component: ProjectFeed,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/documentation",
    name: "Documentation",
    component: Documentation,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/team",
    name: "ProjectTeam",
    component: ProjectTeam,
    beforeEnter: multiguard([requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/inspections",
    name: "Inspections",
    component: Inspections,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/inspections/:id",
    name: "Inspection",
    component: Inspection,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/inspections/:id/meeting/:uuid/",
    name: "InspectionMeeting",
    component: Meeting,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      // requiresAuth: false,
      layout: "default"
    }
  },
  // Inspection in unauthenticated mode
  {
    path: "/inspection/meeting/:uuid",
    component: Meeting,
    name: "InspectionMeetingUnauthenticated",
    meta: {
      layout: "white"
    }
  },
  {
    path: "/project/inspection/:id?/editor",
    name: "InspectionCreateUpdate",
    component: InspectionCreateUpdate,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default"
    }
  },
  // Form Inspection in Meeting
  {
    path: "/project/inspections/:id?",
    name: "InspectionMeetingCreateUpdate",
    component: MeetingCreateUpdate,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/non-conformities",
    name: "NonConformities",
    component: NonConformities,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/non-conformities/:id?/editor",
    name: "NonConformityCreateUpdate",
    component: NonConformityCreateUpdate,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/pcq-dsi",
    name: "PcqDsis",
    component: PcqDsis,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  },
  {
    path: "/project/pcq-dsi/:id?/editor",
    name: "PcqDsiCreateUpdate",
    component: PcqDsiCreateUpdate,
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default"
    }
  },
  {
    path: "/project/notification",
    component: InternalNotification,
    name: "InternalNotification",
    beforeEnter: multiguard([requireAuth, requireProject]),
    meta: {
      layout: "default",
      drawer: ProjectDrawer
    }
  }
  // {
  //   path: "/project/pcq-dsi/:id",
  //   name: "PcqDsi",
  //   component: PcqDsi,
  //   beforeEnter: multiguard([requireAuth, requireProject]),
  //   meta: {
  //     layout: "default"
  //   }
  // },
]
